import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined'
import CloseIcon from '@mui/icons-material/Close'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { ModalOverlay } from '../ModalOverlay'
import type { LabOrderDetail, LabOrderPhoto } from './labOrderTypes'
import {
  MAX_LAB_ORDER_PHOTOS,
  addLabOrderPhoto,
  deleteLabOrderPhoto,
  labOrderPhotoErrorMessage,
  newLocalPhotoId,
  prepareLabOrderPhoto,
  type DraftLabOrderPhoto,
} from './labOrderPhotoUtils'

/** Before the order exists: photos live in the parent's state until create. */
type DraftModeProps = {
  mode: 'draft'
  photos: DraftLabOrderPhoto[]
  onChange: (photos: DraftLabOrderPhoto[]) => void
  /** True while picked photos are still being resized; submit should wait. */
  onPreparingChange?: (preparing: boolean) => void
  disabled?: boolean
}

/** Existing order: every add/remove goes straight to the backend. */
type LiveModeProps = {
  mode: 'live'
  /** The order's Mongo `_id` — order numbers can repeat across labs. */
  orderId: string
  photos: LabOrderPhoto[] | undefined
  /** Receives the updated order detail. Omit for a read-only grid. */
  onOrderUpdated?: (order: LabOrderDetail) => void
}

export type LabOrderPhotosProps = DraftModeProps | LiveModeProps

type Tile = {
  key: string
  src: string | null
  group: 'clinic' | 'lab'
  caption?: string
  removable: boolean
  uploading?: boolean
  removing?: boolean
}

const addButtonClass =
  'inline-flex h-10 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700/50'

function formatPhotoDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function photoCaption(photo: LabOrderPhoto) {
  const name =
    photo.uploadedByName || (photo.uploadedBy === 'lab' ? 'Lab' : 'Clinic')
  return [name, formatPhotoDate(photo.createdAt)].filter(Boolean).join(' · ')
}

function Spinner() {
  return (
    <span
      className="size-6 animate-spin rounded-full border-2 border-white/40 border-t-white"
      aria-hidden
    />
  )
}

function PhotoTile({
  tile,
  onOpen,
  onRemove,
  removeDisabled,
}: {
  tile: Tile
  onOpen: () => void
  onRemove: () => void
  removeDisabled: boolean
}) {
  return (
    <div className="relative aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-700/50">
      {tile.src ? (
        <button
          type="button"
          onClick={onOpen}
          disabled={tile.uploading}
          className="block size-full"
          aria-label="Open photo"
          title={tile.caption}
        >
          <img
            src={tile.src}
            alt=""
            loading="lazy"
            className="size-full object-cover"
          />
        </button>
      ) : (
        <div className="flex size-full flex-col items-center justify-center gap-1 text-slate-400">
          <BrokenImageOutlinedIcon sx={{ fontSize: 24 }} />
          <span className="text-[10px]">Unavailable</span>
        </div>
      )}

      {tile.caption ? (
        <p className="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-2 pb-1 pt-4 text-[10px] text-white">
          {tile.caption}
        </p>
      ) : null}

      {tile.uploading || tile.removing ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/45 text-[11px] font-medium text-white">
          <Spinner />
          {tile.uploading ? 'Uploading…' : 'Removing…'}
        </div>
      ) : null}

      {tile.removable && !tile.removing ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={removeDisabled}
          className="absolute right-0 top-0 flex size-10 items-center justify-center disabled:opacity-50"
          aria-label="Remove photo"
        >
          <span className="flex size-7 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80">
            <CloseIcon sx={{ fontSize: 16 }} />
          </span>
        </button>
      ) : null}
    </div>
  )
}

function PhotoPreview({
  tiles,
  index,
  onIndexChange,
  onClose,
}: {
  tiles: Tile[]
  index: number
  onIndexChange: (index: number) => void
  onClose: () => void
}) {
  const count = tiles.length
  const current = tiles[Math.min(index, count - 1)]

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // Capture phase + stop so a parent modal doesn't close with the preview.
        e.stopPropagation()
        onClose()
      } else if (e.key === 'ArrowLeft' && count > 1) {
        e.stopPropagation()
        onIndexChange((index - 1 + count) % count)
      } else if (e.key === 'ArrowRight' && count > 1) {
        e.stopPropagation()
        onIndexChange((index + 1) % count)
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [count, index, onClose, onIndexChange])

  if (!current?.src) return null

  const navButtonClass =
    'absolute top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20'

  return (
    <ModalOverlay
      className="fixed inset-0 z-[300] flex flex-col bg-black/95"
      role="dialog"
      aria-modal="true"
      aria-label="Photo preview"
    >
      <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3">
        <p className="min-w-0 truncate text-sm text-white/80">
          {count > 1 ? `${index + 1} of ${count}` : 'Photo'}
          {current.caption ? ` · ${current.caption}` : ''}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="flex size-10 shrink-0 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15"
          aria-label="Close"
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </button>
      </div>
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-2 pb-6 sm:px-16"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose()
        }}
      >
        <img
          src={current.src}
          alt=""
          className="max-h-full max-w-full object-contain"
        />
        {count > 1 ? (
          <>
            <button
              type="button"
              onClick={() => onIndexChange((index - 1 + count) % count)}
              className={`${navButtonClass} left-2`}
              aria-label="Previous photo"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
            </button>
            <button
              type="button"
              onClick={() => onIndexChange((index + 1) % count)}
              className={`${navButtonClass} right-2`}
              aria-label="Next photo"
            >
              <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
            </button>
          </>
        ) : null}
      </div>
    </ModalOverlay>
  )
}

/**
 * Photo grid for a lab order: take a photo with the camera or pick from the
 * gallery. Draft mode keeps photos local until the order is created; live
 * mode uploads/removes on the existing order right away.
 */
export default function LabOrderPhotos(props: LabOrderPhotosProps) {
  const draft = props.mode === 'draft' ? props : null
  const live = props.mode === 'live' ? props : null

  const cameraInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const mountedRef = useRef(true)
  const [preparing, setPreparing] = useState(false)
  // Live mode: photos picked but not yet confirmed by the backend.
  const [pending, setPending] = useState<DraftLabOrderPhoto[]>([])
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const savedPhotos = live?.photos ?? []
  const editable = draft ? !draft.disabled : Boolean(live?.onOrderUpdated)
  const busy = preparing || pending.length > 0 || removingId != null
  const count = draft ? draft.photos.length : savedPhotos.length + pending.length
  const atLimit = count >= MAX_LAB_ORDER_PHOTOS
  const canAdd = editable && !busy && !atLimit

  const tiles: Tile[] = draft
    ? draft.photos.map((p) => ({
        key: p.localId,
        src: p.dataUrl,
        group: 'clinic',
        removable: editable,
      }))
    : [
        ...savedPhotos.map(
          (p): Tile => ({
            key: p.id,
            src: p.url,
            group: p.uploadedBy === 'lab' ? 'lab' : 'clinic',
            caption: photoCaption(p),
            removable: editable && p.canDelete,
            removing: removingId === p.id,
          }),
        ),
        ...pending.map(
          (p): Tile => ({
            key: p.localId,
            src: p.dataUrl,
            group: 'clinic',
            removable: false,
            uploading: true,
          }),
        ),
      ]
  const clinicTiles = tiles.filter((t) => t.group === 'clinic')
  const labTiles = tiles.filter((t) => t.group === 'lab')
  const previewTiles = [...clinicTiles, ...labTiles].filter(
    (t) => t.src && !t.uploading,
  )

  async function preparePhotos(files: File[]) {
    const prepared: DraftLabOrderPhoto[] = []
    for (const file of files) {
      try {
        prepared.push({
          localId: newLocalPhotoId(),
          dataUrl: await prepareLabOrderPhoto(file),
        })
      } catch (err) {
        toast.error(
          labOrderPhotoErrorMessage(err, 'Could not read this photo.'),
        )
      }
    }
    return prepared
  }

  async function uploadPhotos(
    orderId: string,
    onOrderUpdated: (order: LabOrderDetail) => void,
    photos: DraftLabOrderPhoto[],
  ) {
    setPending(photos)
    // One at a time keeps the backend's count check simple and the order stable.
    for (const photo of photos) {
      try {
        onOrderUpdated(await addLabOrderPhoto(orderId, photo.dataUrl))
      } catch (err) {
        toast.error(labOrderPhotoErrorMessage(err, 'Failed to upload photo'))
      }
      if (mountedRef.current) {
        setPending((prev) => prev.filter((p) => p.localId !== photo.localId))
      }
    }
  }

  async function handlePicked(input: HTMLInputElement) {
    const files = input.files ? Array.from(input.files) : []
    // Reset so picking the same file again still fires onChange.
    input.value = ''
    if (!files.length || !canAdd) return

    const room = MAX_LAB_ORDER_PHOTOS - count
    const accepted = files.slice(0, room)
    const skipped = files.length - accepted.length
    if (skipped > 0) {
      toast.info(
        `An order can have up to ${MAX_LAB_ORDER_PHOTOS} photos — ${skipped} photo${skipped === 1 ? ' was' : 's were'} skipped.`,
      )
    }

    setPreparing(true)
    draft?.onPreparingChange?.(true)
    const prepared = await preparePhotos(accepted)
    // Tell the parent even if this grid unmounted meanwhile, so submit unlocks.
    draft?.onPreparingChange?.(false)
    if (!mountedRef.current) return
    setPreparing(false)
    if (!prepared.length) return

    if (draft) {
      draft.onChange([...draft.photos, ...prepared])
    } else if (live?.onOrderUpdated) {
      await uploadPhotos(live.orderId, live.onOrderUpdated, prepared)
    }
  }

  async function removePhoto(tile: Tile) {
    if (draft) {
      draft.onChange(draft.photos.filter((p) => p.localId !== tile.key))
      return
    }
    if (!live?.onOrderUpdated) return
    if (!window.confirm('Remove this photo from the order?')) return
    setRemovingId(tile.key)
    try {
      live.onOrderUpdated(await deleteLabOrderPhoto(live.orderId, tile.key))
    } catch (err) {
      toast.error(labOrderPhotoErrorMessage(err, 'Failed to remove photo'))
    } finally {
      if (mountedRef.current) setRemovingId(null)
    }
  }

  function renderGrid(group: Tile[]) {
    return (
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
        {group.map((tile) => (
          <PhotoTile
            key={tile.key}
            tile={tile}
            removeDisabled={busy}
            onOpen={() => {
              const index = previewTiles.findIndex((t) => t.key === tile.key)
              if (index >= 0) setPreviewIndex(index)
            }}
            onRemove={() => removePhoto(tile)}
          />
        ))}
      </div>
    )
  }

  const statusText = preparing
    ? 'Preparing photos…'
    : pending.length > 0
      ? `Uploading ${pending.length} photo${pending.length === 1 ? '' : 's'}…`
      : count > 0
        ? `${count} of ${MAX_LAB_ORDER_PHOTOS} photos`
        : ''

  return (
    <div className="space-y-3">
      {editable || statusText ? (
        <div className="flex flex-wrap items-center gap-2">
          {editable ? (
            <>
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={!canAdd}
                className={addButtonClass}
              >
                <PhotoCameraIcon sx={{ fontSize: 18 }} />
                Take photo
              </button>
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                disabled={!canAdd}
                className={addButtonClass}
              >
                <AddPhotoAlternateIcon sx={{ fontSize: 18 }} />
                Add from gallery
              </button>
              {/* capture opens the rear camera on phones/tablets; desktop gets a file picker. */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handlePicked(e.currentTarget)}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handlePicked(e.currentTarget)}
              />
            </>
          ) : null}
          {statusText ? (
            <span
              className={[
                'text-xs sm:ml-auto',
                atLimit && !busy
                  ? 'font-medium text-amber-700 dark:text-amber-300'
                  : 'text-slate-500',
              ].join(' ')}
            >
              {statusText}
              {atLimit && !busy ? ' — limit reached' : ''}
            </span>
          ) : null}
        </div>
      ) : null}

      {tiles.length === 0 ? (
        live ? (
          <p className="text-sm text-slate-500">No photos yet.</p>
        ) : null
      ) : labTiles.length === 0 ? (
        renderGrid(clinicTiles)
      ) : (
        <div className="space-y-4">
          {clinicTiles.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                From clinic
              </p>
              {renderGrid(clinicTiles)}
            </div>
          ) : null}
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              From lab · finished work
            </p>
            {renderGrid(labTiles)}
          </div>
        </div>
      )}

      {previewIndex != null && previewTiles.length > 0 ? (
        <PhotoPreview
          tiles={previewTiles}
          index={Math.min(previewIndex, previewTiles.length - 1)}
          onIndexChange={setPreviewIndex}
          onClose={() => setPreviewIndex(null)}
        />
      ) : null}
    </div>
  )
}
